import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Locale } from 'date-fns';
import { de, enGB, enUS, fr, pl } from 'date-fns/locale';
import { NzConfigService } from 'ng-zorro-antd/core/config';
import { NzDateAdapter } from 'ng-zorro-antd/core/time';
import { de_DE, en_GB, fr_FR, NzI18nService, pl_PL } from 'ng-zorro-antd/i18n';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE } from '../lib/language-preference-storage';
import { Currency, CurrencySymbol } from '../types/currency';
import {
  BackendLangType,
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_SYMBOL,
  DEFAULT_DATE_FORMATS,
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_LANGUAGE_TYPE,
  DEFAULT_TIMEZONE,
  EntityLangType,
  LangType,
  normalizeLanguagePreference,
  SHOW_TRANSLATION_KEY,
  SUPPORTED_LANGUAGE_TYPES,
} from '../types/language';
import { DistanceUnit } from '../types/localization-setting';
import {
  defaultLocalizationStateModel,
  LocalizationStateModel,
} from '../types/localization-state';
import { toBackendLangType, toEntityLangType } from '../utils/transform';
import {
  ApplyLocalizationSetting,
  SelectLanguage,
} from './localization.actions';

@State<LocalizationStateModel>({
  name: 'localization',
  defaults: defaultLocalizationStateModel,
})
@Injectable()
export class LocalizationState {
  private readonly nzConfigService = inject(NzConfigService);
  private readonly translateService = inject(TranslateService);
  private readonly dateConfig = inject(DateFnsConfigurationService);
  private readonly i18n = inject(NzI18nService);
  private readonly dateAdapter = inject(NzDateAdapter);
  private readonly userPreferenceStorage = inject(
    USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE,
  );

  @Selector()
  static getDefaultTimezone(state: LocalizationStateModel): string {
    return state.localizationSetting?.defaultTimezone ?? DEFAULT_TIMEZONE;
  }

  @Selector()
  static getDistanceUnit(state: LocalizationStateModel): DistanceUnit {
    return state.localizationSetting?.distanceUnit ?? DEFAULT_DISTANCE_UNIT;
  }

  @Selector()
  static getCurrencyCode(state: LocalizationStateModel): Currency {
    return state.localizationSetting?.currencyCode ?? DEFAULT_CURRENCY;
  }

  @Selector()
  static getCurrencySymbol(state: LocalizationStateModel): CurrencySymbol {
    return state.localizationSetting?.currencySymbol ?? DEFAULT_CURRENCY_SYMBOL;
  }

  @Selector()
  static getLang(state: LocalizationStateModel): LangType {
    return state.lang;
  }

  @Selector()
  static getBackendLang(state: LocalizationStateModel): BackendLangType {
    return toBackendLangType(state.lang);
  }

  @Selector()
  static getEntityLang(state: LocalizationStateModel): EntityLangType {
    return toEntityLangType(state.lang);
  }

  @Selector()
  static getDateFnsLocale(state: LocalizationStateModel): Locale {
    return state.dateFnsLocale;
  }

  @Selector()
  static getLocalizationSetting(
    state: LocalizationStateModel,
  ): LocalizationStateModel['localizationSetting'] {
    return state.localizationSetting;
  }

  @Selector()
  static getLocalizedDateTimeFormat(state: LocalizationStateModel): string {
    return (
      state.localizationSetting?.dateTimeFormat ??
      DEFAULT_DATE_FORMATS.dateTimeFormat
    );
  }

  @Selector()
  static getLocalizedDateFormat(state: LocalizationStateModel): string {
    return (
      state.localizationSetting?.dateFormat ?? DEFAULT_DATE_FORMATS.dateFormat
    );
  }

  @Selector()
  static getLocalizedTimeFormat(state: LocalizationStateModel): string {
    return (
      state.localizationSetting?.timeFormat ?? DEFAULT_DATE_FORMATS.timeFormat
    );
  }

  @Selector()
  static getLocalizedDayMonthFormat(state: LocalizationStateModel): string {
    return (
      state.localizationSetting?.dayMonthFormat ??
      DEFAULT_DATE_FORMATS.dayMonthFormat
    );
  }

  @Selector()
  static getLocalizedMonthYearFormat(state: LocalizationStateModel): string {
    return (
      state.localizationSetting?.monthYearFormat ??
      DEFAULT_DATE_FORMATS.monthYearFormat
    );
  }

  @Selector()
  static getLocale(state: LocalizationStateModel): string {
    return state.localizationSetting?.locale ?? DEFAULT_LANGUAGE_TYPE;
  }

  async ngxsOnInit(ctx: StateContext<LocalizationStateModel>): Promise<void> {
    const initialLang = this.getInitialLang();
    this.patchLanguage(ctx, initialLang);
  }

  @Action(SelectLanguage)
  selectLanguage(
    ctx: StateContext<LocalizationStateModel>,
    action: SelectLanguage,
  ): void {
    this.patchLanguage(ctx, action.lang);
  }

  @Action(ApplyLocalizationSetting)
  applyLocalizationSetting(
    ctx: StateContext<LocalizationStateModel>,
    action: ApplyLocalizationSetting,
  ): void {
    const setting = action.localizationSetting ?? null;
    ctx.patchState({ localizationSetting: setting });
    const orgUnitLocale = setting?.locale;
    const stored = this.userPreferenceStorage.getUserPreferredLanguage();
    const normalizedStored = normalizeLanguagePreference(stored);
    const lang =
      normalizedStored !== null
        ? normalizedStored
        : this.getSystemLanguage(orgUnitLocale);
    this.patchLanguage(ctx, lang);
    this.updateTimePickerFormat(ctx);
  }

  private updateTimePickerFormat(
    ctx: StateContext<LocalizationStateModel>,
  ): void {
    this.nzConfigService.set('timePicker', {
      nzFormat: LocalizationState.getLocalizedTimeFormat(ctx.getState()),
    });
  }

  /**
   * Resolves the language to use on init: user preference (if set) else system language.
   */
  private getInitialLang(): LangType {
    const stored = this.userPreferenceStorage.getUserPreferredLanguage();
    const normalizedStored = normalizeLanguagePreference(stored);
    if (normalizedStored !== null) {
      return normalizedStored;
    }
    return this.getSystemLanguage();
  }

  /**
   * Language when the user has not explicitly selected one.
   * Order: org-unit locale (if provided and supported) → browser → app default.
   */
  private getSystemLanguage(orgUnitLocale?: string): LangType {
    let language: LangType = DEFAULT_LANGUAGE_TYPE;

    const normalized = orgUnitLocale?.replace('_', '-');
    if (
      normalized &&
      SUPPORTED_LANGUAGE_TYPES.includes(normalized as LangType)
    ) {
      language = normalized as LangType;
    } else if (typeof window !== 'undefined') {
      const browserCulture = this.translateService.getBrowserCultureLang();
      if (
        browserCulture &&
        SUPPORTED_LANGUAGE_TYPES.includes(browserCulture as LangType)
      ) {
        language = browserCulture as LangType;
      } else {
        const browserLang = this.translateService.getBrowserLang();
        const mapped = browserLang
          ? SUPPORTED_LANGUAGE_TYPES.find((lang) =>
              lang.startsWith(browserLang),
            )
          : null;
        language = mapped ?? DEFAULT_LANGUAGE_TYPE;
      }
    }

    return language;
  }

  private patchLanguage(
    ctx: StateContext<LocalizationStateModel>,
    lang: LocalizationStateModel['lang'],
  ): void {
    const resolved: LangType =
      normalizeLanguagePreference(lang) ?? DEFAULT_LANGUAGE_TYPE;

    this.translateService.use(resolved);
    this.translateService.setFallbackLang(DEFAULT_LANGUAGE_TYPE);
    let dateFnsLocale: Locale;

    switch (resolved) {
      case SHOW_TRANSLATION_KEY:
        this.translateService.setFallbackLang(SHOW_TRANSLATION_KEY);
        dateFnsLocale = enUS;
        break;
      case 'fr-FR':
        this.i18n.setLocale(fr_FR);
        this.dateAdapter.setLocale(fr);
        this.dateConfig.setLocale(fr);
        dateFnsLocale = fr;
        break;
      case 'pl-PL':
        this.i18n.setLocale(pl_PL);
        this.dateAdapter.setLocale(pl);
        this.dateConfig.setLocale(pl);
        dateFnsLocale = pl;
        break;
      case 'de-DE':
        this.i18n.setLocale(de_DE);
        this.dateAdapter.setLocale(de);
        this.dateConfig.setLocale(de);
        dateFnsLocale = de;
        break;
      case 'en-GB':
      default:
        this.i18n.setLocale(en_GB);
        this.dateAdapter.setLocale(enGB);
        this.dateConfig.setLocale(enGB);
        dateFnsLocale = enGB;
        break;
    }

    ctx.patchState({
      lang: resolved,
      dateFnsLocale,
    });
  }
}
